<template>
    <PhuConfiguration v-if="configuration" />
    <div v-else id="phu-production" class="phu-production" :class="[$mq, { 'phu-production--collapsed': !sidebarExpanded }]">
        <div class="phu-production__sidebar">
            <PhuSidebar
                :recommendations="recommendations"
                :products="products"
                :summary="summary"
                :now="now"
                :collapsed="!sidebarExpanded"
                :configurable="!fixedLayout"
                @apply="applyRecommendation"
                @add-product="openAddProduct"
                @toggle="toggleSidebar"
                @settings="openConfiguration"
            />
        </div>

        <main v-if="forecastMissing" class="phu-production__board">
            <Init :date="forecastDate" />
        </main>
        <main v-else class="phu-production__board">
            <PhuTopbar
                :active-tab="activeTab"
                :counters="counters"
                :workstations="workstations"
                :active-workstation-id="activeWorkstationId"
                :searchable="activeTab === tabs.STOCK"
                :search="stockSearch"
                :show-time="showSlotTime"
                :show-demo="showDemoSale"
                :demo-products="holdingProducts"
                :demo-busy="demoBusy"
                @demo-sale="runDemoSale"
                @change-tab="setActiveTab"
                @change-workstation="setActiveWorkstation"
                @toggle-time="showSlotTime = !showSlotTime"
                @search="stockSearch = $event"
            />

            <div class="phu-production__content">
                <CookingTab
                    v-if="activeTab === tabs.COOKING"
                    :equipment="equipment"
                    :catalog="equipmentCatalog"
                    :products="products"
                    :now="now"
                    :highlight-workstation-id="highlightWorkstationId"
                    @toggle="toggleEquipment"
                />
                <HoldingTab
                    v-else-if="activeTab === tabs.HOLDING"
                    :phus="phus"
                    :products="products"
                    :now="now"
                    :show-time="showSlotTime"
                    :fixed-layout="fixedLayout"
                    @slot-click="onSlotClick"
                    @save-positions="saveSlotPositions"
                    @move-across="moveSlotAcross"
                    @configure="openConfiguration"
                />
                <StockTab v-else :stock="stock" :products="products" :search="stockSearch" @waste="openStockWaste" />
            </div>
        </main>
    </div>
</template>

<script>
import PhuSidebar from '@/components/domain/phu-production/sidebar/PhuSidebar.vue'
import PhuTopbar from '@/components/domain/phu-production/topbar/PhuTopbar.vue'
import CookingTab from '@/components/domain/phu-production/cooking/CookingTab.vue'
import HoldingTab from '@/components/domain/phu-production/holding/HoldingTab.vue'
import StockTab from '@/components/domain/phu-production/stock/StockTab.vue'
import PhuConfiguration from '@/components/domain/phu-production/configuration/PhuConfiguration.vue'
import Init from '@/components/domain/pls/init.vue'
import { PHU_TABS } from '@/store/modules/phuProduction'
import { BLOCKED_REASONS, WORKSTATIONS } from '@/store/static/phuProduction'
import { resolveSlotState } from '@/components/domain/phu-production/utils/slotState'
import { SLOT_STATES } from '@/store/static/phuProduction'

const TICK_MS = 1000
const SIDEBAR_STORAGE_KEY = 'phuSidebarExpanded'
const PORTRAIT_MAX_WIDTH = 901

function initialSidebarExpanded() {
    if (window.innerWidth <= PORTRAIT_MAX_WIDTH) {
        return false
    }
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false'
}
const TAB_STORAGE_KEY = 'phuActiveTab'
const SNACKBAR_TIMEOUT = 5000
const HIGHLIGHT_MS = 3000

export default {
    name: 'PhuProduction',
    components: { PhuSidebar, PhuTopbar, CookingTab, HoldingTab, StockTab, PhuConfiguration, Init },
    data() {
        return {
            tabs: PHU_TABS,
            now: Date.now(),
            tickInterval: null,
            sidebarExpanded: initialSidebarExpanded(),
            stockSearch: '',
            showSlotTime: false,
            demoBusy: false,
            highlightWorkstationId: null,
            highlightTimer: null
        }
    },
    computed: {
        activeTab() {
            return this.$store.getters['phuProduction/getActiveTab']
        },
        activeWorkstationId() {
            return this.$store.getters['phuProduction/getActiveWorkstationId']
        },
        workstations() {
            return this.$store.getters['phuProduction/getWorkstations']
        },
        products() {
            return this.$store.getters['phuProduction/getProducts']
        },
        recommendations() {
            return this.$store.getters['phuProduction/getRecommendations']
        },
        equipment() {
            return this.$store.getters['phuProduction/getEquipment'](this.now)
        },
        equipmentCatalog() {
            return this.$store.getters['phuProduction/getEquipmentCatalog']
        },
        phus() {
            return this.$store.getters['phuProduction/getPhus']
        },
        stock() {
            return this.$store.getters['phuProduction/getAllStock'](this.now)
        },
        summary() {
            return this.$store.getters['phuProduction/getSummary']
        },
        counters() {
            return this.$store.getters['phuProduction/getTabCounters'](this.now)
        },
        configuration() {
            return this.$store.getters['phuProduction/isConfiguration']
        },
        fixedLayout() {
            return this.$store.getters['phuProduction/isFixedLayout']
        },
        fifoSales() {
            return this.$store.getters['phuProduction/isFifoSales']
        },
        showDemoSale() {
            return this.fifoSales && this.activeTab === this.tabs.HOLDING
        },
        holdingProducts() {
            const seen = {}
            const products = []
            this.phus.forEach((phu) => {
                ;(phu.slots || []).forEach((slot) => {
                    const state = resolveSlotState(slot, this.now)
                    const inHolding = state === SLOT_STATES.READY || state === SLOT_STATES.ABOUT_TO_EXPIRE
                    if (!inHolding || !slot.product_id || seen[slot.product_id]) {
                        return
                    }
                    seen[slot.product_id] = true
                    const product = this.products[slot.product_id]
                    products.push({
                        id: slot.product_id,
                        name: product && product.name ? product.name : String(slot.product_id)
                    })
                })
            })
            return products
        },
        forecastMissing() {
            return this.$store.getters['phuProduction/isForecastMissing']
        },
        forecastDate() {
            return this.$store.getters['phuProduction/getForecastDate']
        },
        addProductList() {
            return Object.values(this.products).reduce((acc, product) => {
                if (product.status) {
                    const blockedLabel = this.noFreeEquipmentLabel(product)
                    acc[product.id] = {
                        id: product.id,
                        name: product.name,
                        image: product.image,
                        disabled: Boolean(blockedLabel),
                        disabledLabel: blockedLabel
                    }
                }
                return acc
            }, {})
        }
    },
    methods: {
        setActiveTab(tab) {
            if (!Object.values(PHU_TABS).includes(tab)) {
                return
            }
            this.$store.commit('phuProduction/setActiveTab', tab)
            localStorage.setItem(TAB_STORAGE_KEY, tab)
        },
        restoreActiveTab() {
            const saved = localStorage.getItem(TAB_STORAGE_KEY)
            this.setActiveTab(saved || PHU_TABS.COOKING)
        },
        setActiveWorkstation(workstationId) {
            this.$store.commit('phuProduction/setActiveWorkstation', workstationId)
        },
        openConfiguration() {
            if (this.fixedLayout) {
                return
            }
            this.$store.commit('phuProduction/setConfiguration', true)
        },
        toggleSidebar() {
            this.sidebarExpanded = !this.sidebarExpanded
            localStorage.setItem(SIDEBAR_STORAGE_KEY, String(this.sidebarExpanded))
        },
        load() {
            this.$overlay.loading()
            this.$store
                .dispatch('phuProduction/loadData')
                .catch(() => this.showSyncError())
                .then(() => this.$overlay.hide())
        },
        tick() {
            const previousNow = this.now
            this.now = Date.now()
            this.announceFinishedCooking(previousNow)
            if (new Date(this.now).getSeconds() !== 0 || this.configuration) {
                return
            }
            this.$store.dispatch('phuProduction/loadForecast', { background: true })
            this.$store.dispatch('phuProduction/refreshHoldingUnits')
        },
        showSyncError() {
            this.$snackbar.error({
                message: this.$t('phu_production.errors.sync'),
                closeable: true,
                timeout: SNACKBAR_TIMEOUT
            })
        },
        runDemoSale(payload) {
            if (this.demoBusy) {
                return
            }
            this.demoBusy = true
            this.$store.dispatch('phuProduction/demoSale', payload).catch(() => {
                this.showSyncError()
            }).finally(() => {
                this.demoBusy = false
            })
        },
        productName(productId) {
            const product = this.products[productId]
            return product ? product.name : ''
        },
        applyRecommendation(recommendation) {
            this.openCookQuantity(recommendation)
        },
        openAddProduct() {
            const products = Object.values(this.addProductList)
            if (products.length && products.every((item) => item.disabled)) {
                const sample = this.products[products[0].id]
                this.showNoEquipment(sample && sample.workstation_id)
                return
            }
            this.$popup.phuProduct({
                list: products,
                data: { hideEmpty: true },
                callSave: (popup) => {
                    const productId = popup.selectedEl
                    return Promise.resolve().then(() => {
                        setTimeout(() => {
                            if (productId) {
                                this.openCookQuantity({ id: null, batch_id: null, product_id: productId, units: 1 })
                            }
                        }, 0)
                    })
                }
            })
        },
        noFreeEquipmentLabel(product) {
            if (!product || this.$store.getters['phuProduction/hasAvailableEquipment'](product.workstation_id, this.now)) {
                return ''
            }
            const workstation = WORKSTATIONS.find((item) => item.id === product.workstation_id)
            return this.$t(`phu_production.blocked.no_free.${workstation ? workstation.type : 'fryer'}`)
        },
        openCookQuantity(recommendation) {
            const product = this.products[recommendation.product_id]
            if (this.noFreeEquipmentLabel(product)) {
                this.showNoEquipment(product.workstation_id)
                return
            }
            const maxUnits = product && product.holding_max_units ? product.holding_max_units : 10
            this.$popup.phuWaste({
                customClass: 'phu-cook-quantity',
                title: this.$t('phu_production.add_product.quantity_title'),
                productName: product ? product.name : '',
                productUrl: product && product.image ? product.image : '',
                productMessage: this.$t('phu_production.add_product.quantity_message'),
                inputVal: Math.min(recommendation.units, maxUnits),
                numericNewDesing: { max: maxUnits },
                callSave: (popup) => {
                    const units = parseInt(popup.inputVal, 10)
                    if (!units) {
                        return
                    }
                    setTimeout(() => this.openAssign(recommendation, units), 0)
                }
            })
        },
        openAssign(recommendation, units) {
            const product = this.products[recommendation.product_id]
            if (!product || !this.$store.getters['phuProduction/hasAvailableEquipment'](product.workstation_id, this.now)) {
                this.showNoEquipment(product && product.workstation_id)
                return
            }
            const phus = this.phusForProduct(product)
            if (!phus.length || !this.hasCompatibleSlot(product)) {
                this.showNoSlot(product)
                return
            }
            this.$popup.phuAssign({
                list: phus,
                data: {
                    productId: product.id,
                    units,
                    workstationId: product.workstation_id,
                    workstations: [{ id: product.workstation_id, name: this.equipmentName(product) }],
                    products: this.products,
                    now: this.now
                },
                callSave: (popup) => this.startCooking(recommendation, units, popup.selectedEl)
            })
        },
        phusForProduct(product) {
            return this.$store.getters['phuProduction/getAllPhus'].filter((phu) => {
                return phu.workstation_id === product.workstation_id && phu.slots.some((slot) => String(slot.product_id) === String(product.id))
            })
        },
        hasCompatibleSlot(product) {
            return this.$store.getters['phuProduction/getAllPhus'].some((phu) => phu.slots.some((slot) => {
                return String(slot.product_id) === String(product.id) && !slot.batch_id && !Number(slot.units)
            }))
        },
        startCooking(recommendation, units, slotId) {
            const product = this.products[recommendation.product_id]
            return this.$store.dispatch('phuProduction/applyRecommendation', { recommendation, units, slotId }).then(() => {
                this.showCookingAssigned(product, units, slotId)
            }).catch((error) => {
                const reason = error ? error.message : ''
                if (reason === BLOCKED_REASONS.NO_COOKING_CAPACITY) {
                    return () => this.showNoEquipment(product && product.workstation_id)
                }
                if (reason === BLOCKED_REASONS.NO_PHU_CAPACITY) {
                    return () => this.showNoSlot(product)
                }
                this.showSyncError()
            })
        },
        announceFinishedCooking(previousNow) {
            const finished = []
            this.$store.getters['phuProduction/getAllPhus'].forEach((phu) => {
                phu.slots.forEach((slot) => {
                    if (resolveSlotState(slot, previousNow) !== SLOT_STATES.COOKING) {
                        return
                    }
                    const state = resolveSlotState(slot, this.now)
                    const inHolding = state === SLOT_STATES.READY || state === SLOT_STATES.ABOUT_TO_EXPIRE
                    if (!inHolding) {
                        return
                    }
                    finished.push({ slot, phu })
                })
            })
            if (!finished.length) {
                return
            }
            const message = finished.map(({ slot, phu }) => this.$t('phu_production.assign.ready', {
                product: this.productName(slot.product_id),
                units: slot.units,
                phu: phu.name
            })).join(' · ')
            this.$snackbar.success({
                message,
                closeable: true,
                duration: SNACKBAR_TIMEOUT
            })
        },
        showCookingAssigned(product, units, slotId) {
            this.$snackbar.success({
                message: this.$t('phu_production.assign.started', {
                    product: product ? product.name : '',
                    units,
                    phu: this.phuName(slotId)
                }),
                closeable: true,
                duration: SNACKBAR_TIMEOUT
            })
        },
        phuName(slotId) {
            const phu = this.$store.getters['phuProduction/getAllPhus'].find((item) => {
                return item.slots.some((slot) => String(slot.id) === String(slotId))
            })
            return phu ? phu.name : ''
        },
        equipmentName(product) {
            const workstation = this.workstations.find((item) => product && item.id === product.workstation_id)
            const catalog = this.equipmentCatalog.find((item) => workstation && item.type === workstation.type)
            if (catalog) {
                return catalog.name
            }
            return workstation ? workstation.name : this.$t('phu_production.equipment.title')
        },
        showNoEquipment(workstationId) {
            this.$popup.phuNoEquipment({
                title: this.$t('phu_production.no_equipment.title'),
                message: this.$t('phu_production.no_equipment.message'),
                callSave: () => {
                    if (workstationId) {
                        this.setActiveWorkstation(workstationId)
                    }
                    this.setActiveTab(this.tabs.COOKING)
                    this.highlightBusyEquipment(workstationId)
                }
            })
        },
        highlightBusyEquipment(workstationId) {
            clearTimeout(this.highlightTimer)
            this.highlightWorkstationId = workstationId || null
            this.highlightTimer = setTimeout(() => {
                this.highlightWorkstationId = null
            }, HIGHLIGHT_MS)
        },
        showNoSlot(product) {
            this.$popup.messageNewDesing({
                customClass: 'pls-stock-popup',
                title: this.$t('phu_production.no_slot.title'),
                message: this.$t('phu_production.no_slot.message', { product: product ? product.name : '' }),
                textSave: this.$t('phu_production.common.close')
            })
        },
        toggleEquipment(equipment) {
            this.$store.dispatch('phuProduction/toggleEquipment', equipment).catch(() => this.showSyncError())
        },
        saveSlotPositions(params) {
            this.$store.dispatch('phuProduction/saveSlotPositions', params).catch(() => this.showSyncError())
        },
        moveSlotAcross(payload) {
            this.$store.dispatch('phuProduction/transferSlotBetweenPhus', payload).catch(() => this.showSyncError())
        },
        onSlotClick(slot) {
            if (resolveSlotState(slot, this.now) !== SLOT_STATES.EXPIRED) {
                return
            }
            const stockItem = this.stock.find((item) => item.product_id === slot.product_id)
            const onHand = Math.max(0, Math.floor(Number(stockItem && stockItem.units) || 0))
            const available = Math.min(Number(slot.units) || 0, onHand)
            if (available <= 0) {
                return
            }
            const product = this.products[slot.product_id]
            this.$popup.phuWaste({
                productName: this.productName(slot.product_id),
                productUrl: product && product.image ? product.image : '',
                wasteUnitsPrompt: true,
                inputVal: available,
                numericNewDesing: { max: available },
                callSave: (popup) => {
                    const units = parseInt(popup.inputVal, 10)
                    if (!units || units <= 0 || units > available) {
                        return
                    }
                    const partial = units < available
                    if (partial && typeof slot.reduce === 'function') {
                        slot.reduce(units)
                    } else if (!partial && typeof slot.release === 'function') {
                        slot.release()
                    }
                    this.$store.dispatch('phuProduction/registerSlotWaste', { slot, units }).then((saved) => {
                        if (!saved) {
                            if (typeof slot.restore === 'function') {
                                slot.restore()
                            }
                            this.showSyncError()
                            return
                        }
                        this.showSlotWasteRecorded(this.productName(slot.product_id), saved)
                    }).catch(() => {
                        if (typeof slot.restore === 'function') {
                            slot.restore()
                        }
                        this.showSyncError()
                    })
                }
            })
        },
        openStockWaste(item) {
            const product = this.products[item.product_id]
            const name = this.productName(item.product_id)
            const onHand = Math.max(0, Math.floor(Number(item.units) || 0))
            const max = onHand > 0 ? onHand : 999
            this.$popup.phuWaste({
                productName: name,
                productUrl: product && product.image ? product.image : '',
                inputVal: onHand > 0 ? onHand : 1,
                numericNewDesing: { max },
                callSave: (popup) => {
                    const units = parseInt(popup.inputVal, 10)
                    if (!units || units <= 0 || units > max) {
                        return
                    }
                    this.$store.dispatch('phuProduction/registerStockWaste', { productId: item.product_id, units }).then((saved) => {
                        if (!saved) {
                            return
                        }
                        this.showWasteRecorded(name, saved)
                    }).catch(() => this.showSyncError())
                }
            })
        },
        escapeHtml(value) {
            return String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
        },
        showSlotWasteRecorded(name, waste) {
            this.$snackbar.error({
                customClass: 'phu-waste-toast',
                html: true,
                closeable: true,
                message: this.$t('phu_production.stock.recorded', {
                    product: this.escapeHtml(name),
                    units: waste.units
                }),
                actionType: 'button',
                textButton: this.$t('popup.sign.text_undo'),
                duration: SNACKBAR_TIMEOUT,
                action: () => {
                    this.$store.dispatch('phuProduction/undoSlotWaste', waste).catch(() => this.showSyncError())
                }
            })
        },
        showWasteRecorded(name, waste) {
            const label = `${this.escapeHtml(name.toUpperCase())} x${waste.units}`
            this.$snackbar.error({
                customClass: 'phu-waste-toast',
                html: true,
                closeable: true,
                message: `<b>${label}</b> ${this.$t('phu_production.stock.recorded_rest')}`,
                actionType: 'button',
                textButton: this.$t('popup.sign.text_undo'),
                duration: SNACKBAR_TIMEOUT,
                action: () => {
                    this.$store.dispatch('phuProduction/undoStockWaste', waste).catch(() => this.showSyncError())
                }
            })
        }
    },
    created() {
        this.restoreActiveTab()
        this.load()
        this.tickInterval = setInterval(this.tick, TICK_MS)
    },
    beforeDestroy() {
        clearInterval(this.tickInterval)
        clearTimeout(this.highlightTimer)
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.phu-production {
    display: grid;
    grid-template-columns: 300px auto;
    grid-template-rows: 1fr;
    grid-template-areas: 'sidebar board';
    gap: $phu-gap;
    width: 100%;
    height: 100%;
    padding: 8px;
    background-color: $phu-board;

    &__sidebar {
        grid-area: sidebar;
        height: 100%;
        min-height: 0;
    }

    &__board {
        grid-area: board;
        @include display-flex();
        @include flex-direction(column);
        gap: $phu-gap;
        min-width: 0;
        height: 100%;
        overflow: hidden;
    }

    &__content {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
    }

    &--collapsed {
        grid-template-columns: 57px auto;
    }

    &.landscape {
        grid-template-columns: 250px auto;

        &.phu-production--collapsed {
            grid-template-columns: 45px auto;
        }
    }
}
</style>
